import type { OptimizelyDecision } from '@optimizely/optimizely-sdk'
import type { OptimizelyDecision as OptimizelyDecisionReact } from '@optimizely/react-sdk'
import {
  type FeatureFlagCookies,
  getFeatureFlagCookieOverride,
} from './feature-flag-cookie-override'

export type TransformedFeatureFlag = {
  enabled: boolean
  disabledGTMEvent: boolean
  variationKey: string | null | undefined
  variables: Record<string, any> | undefined
  flagKey: string | undefined
}

/**
 * Transforms raw Optimizely decision data + cookies into a standardized
 * `TransformedFeatureFlag` format for consistent consumption across the app.
 *
 * Prioritizes **cookie overrides** over Optimizely decision, then falls back
 * to Optimizely's `enabled` state. Logs warnings for Optimizely decision reasons.
 *
 * @param optimizelyDecision - Raw Optimizely decision object from `useDecision`
 * @param cookies - Cookie data in FeatureFlagCookies format
 * @returns Standardized feature flag data or `undefined` if no `flagKey`
 *
 * @remarks
 * **Processing priority:**
 * 1. **Cookie override** (highest priority, disables GTM events)
 * 2. **Optimizely `enabled`** (default behavior)
 * 3. **No `flagKey`** → returns `undefined`
 *
 * Cookie override takes precedence and sets `disabledGTMEvent: true`.
 */
export function transformFeatureFlag(
  optimizelyDecision: OptimizelyDecision | OptimizelyDecisionReact | undefined,
  cookies: FeatureFlagCookies
): TransformedFeatureFlag | Record<string, never> {
  const { enabled, variationKey, variables, flagKey, reasons } = optimizelyDecision ?? {}

  if (!optimizelyDecision) {
    return {
      enabled: false,
      disabledGTMEvent: false,
      variationKey: null,
      variables: {},
      flagKey: '',
    }
  }

  const featureFlagCookieOverride = flagKey ? getFeatureFlagCookieOverride(flagKey, cookies) : false

  if (reasons && reasons.length > 0) {
    // eslint-disable-next-line no-console
    console.warn(reasons.join(' '))
  }

  const commonFlagData = {
    variationKey,
    variables,
    flagKey,
  }

  if (featureFlagCookieOverride !== null) {
    return {
      ...commonFlagData,
      enabled: featureFlagCookieOverride,
      disabledGTMEvent: true,
    }
  }

  return {
    ...commonFlagData,
    enabled: !!enabled,
    disabledGTMEvent: false,
  }
}
